import { useState, useMemo, lazy, Suspense } from 'react';
import { PageShell } from '../components/PageShell';
import { Button } from '@weretradeit/ui-react';
import '../styles/project-map.css';
import projectMapData from '../data/project-map-data.json';
import { GraphNode, GraphLink } from '../hooks/useD3ForceGraph';

// Code splitting: Lazy load heavy components
const ProjectMapCanvas = lazy(() => import('../components/ProjectMapCanvas').then(m => ({ default: m.ProjectMapCanvas })));
const ProjectMapControls = lazy(() => import('../components/ProjectMapControls').then(m => ({ default: m.ProjectMapControls })));
const ProjectMapLegend = lazy(() => import('../components/ProjectMapLegend').then(m => ({ default: m.ProjectMapLegend })));
const NodeInfoPanel = lazy(() => import('../components/NodeInfoPanel').then(m => ({ default: m.NodeInfoPanel })));
const ProjectMapTimeline = lazy(() => import('../components/ProjectMapTimeline').then(m => ({ default: m.ProjectMapTimeline })));

interface ProjectMapData {
  metadata: {
    generated: string;
    version: string;
    source: string;
  };
  summary: {
    totalNodes: number;
    totalLinks: number;
    nodeTypes: {
      worker: number;
      pages_app: number;
      external_app: number;
      shared_module: number;
    };
  };
  nodes: GraphNode[];
  links: GraphLink[];
}

export function ProjectMapPage() {
  const [data] = useState<ProjectMapData>(projectMapData as ProjectMapData);
  const [error, setError] = useState<string | null>(null);
  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null);
  const [hoveredNode, setHoveredNode] = useState<GraphNode | null>(null);
  const [filters, setFilters] = useState({
    worker: true,
    pages_app: true,
    external_app: true,
    shared_module: true,
  });

  // Performance optimization: Memoize filtered data to avoid recalculating on every render
  const filteredData = useMemo(() => {
    const filteredNodes = data.nodes.filter((node) => filters[node.type]);
    const filteredNodeIds = new Set(filteredNodes.map((n) => n.id));

    const filteredLinks = data.links.filter((link) => {
      const sourceId = typeof link.source === 'string' ? link.source : link.source.id;
      const targetId = typeof link.target === 'string' ? link.target : link.target.id;
      return filteredNodeIds.has(sourceId) && filteredNodeIds.has(targetId);
    });

    return {
      nodes: filteredNodes,
      links: filteredLinks,
      summary: {
        ...data.summary,
        totalNodes: filteredNodes.length,
        totalLinks: filteredLinks.length,
      },
    };
  }, [data, filters]);

  // Performance optimization: Empty snapshots array for now (will be populated from git hook)
  const snapshots = useMemo(() => [], []);

  if (error) {
    return (
      <PageShell
        pageTitle="Project Structure Map"
        breadcrumbs={[
          { label: 'Home', href: '/' },
          { label: 'Service System', href: '/' },
          { label: 'Project Map' },
        ]}
      >
        <div className="empty-state">
          <div className="empty-state-icon">⚠️</div>
          <h2>Error loading project map</h2>
          <p>{error}</p>
          <Button onClick={() => window.location.reload()}>Retry</Button>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell
      pageTitle="Project Structure Map"
      breadcrumbs={[
        { label: 'Home', href: '/' },
        { label: 'Service System', href: '/' },
        { label: 'Project Map' },
      ]}
    >
      <Suspense fallback={<div className="loading-state">Loading project map...</div>}>
        <div className="project-map-page">
          {/* Header */}
          <div className="project-map-header">
            <h1>🗺️ Project Structure Map</h1>
            <p className="text-muted">
              Interactive visualization of workers, apps, shared modules, and their dependencies
            </p>
            <div className="header-actions">
              <span className="last-updated">
                Last updated: {new Date(data.metadata.generated).toLocaleString()}
              </span>
            </div>
          </div>

          {/* Main Layout: Canvas + Sidebar */}
          <div className="project-map-main-layout">
            {/* Left: Graph Canvas */}
            <Suspense fallback={<div className="canvas-loading">Loading graph...</div>}>
              <ProjectMapCanvas
                nodes={filteredData.nodes}
                links={filteredData.links}
                filters={filters}
                onNodeClick={setSelectedNode}
                onNodeHover={setHoveredNode}
              />
            </Suspense>

            {/* Right: Controls + Legend + Timeline + Node Info */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <Suspense fallback={<div className="control-loading">Loading controls...</div>}>
                <ProjectMapControls
                  filters={filters}
                  onFilterChange={setFilters}
                  onRefresh={() => window.location.reload()}
                  lastUpdated={data.metadata.generated}
                  totalNodes={filteredData.summary.totalNodes}
                  totalLinks={filteredData.summary.totalLinks}
                />
              </Suspense>

              <Suspense fallback={<div className="legend-loading">Loading legend...</div>}>
                <ProjectMapLegend />
              </Suspense>

              <Suspense fallback={<div className="timeline-loading">Loading timeline...</div>}>
                <ProjectMapTimeline currentData={filteredData} snapshots={snapshots} />
              </Suspense>

              {selectedNode && (
                <Suspense fallback={<div className="info-loading">Loading info...</div>}>
                  <NodeInfoPanel node={selectedNode} allLinks={data.links} onClose={() => setSelectedNode(null)} />
                </Suspense>
              )}
            </div>
          </div>
        </div>
      </Suspense>
    </PageShell>
  );
}
